import React, { useState, useRef, useEffect, useCallback } from 'react';
import s from './s.module.less';
import { EAppointmentContactTypeDictKey, Prospect, TAppointmentItem } from 'types/common';
import dayjs from 'dayjs';
import { getCalendarAppointmentEventDetail } from 'api/google';
import { EIntakeFormStatus, TServerCalendarAppointmentEventDetail } from 'types/calendar';
import { getTimeStr } from 'utils/calendar';
import { PATH } from 'constants/path';
import { Button, message, Tooltip } from 'antd';
import capitalize from 'lodash/capitalize';
import useTimezone from 'hooks/useTimezone';
import { sendMessage } from 'api/inbox';
import { getUserId } from 'utils/localstore';
import { getIntakeFormUrlByAppointmentId } from 'utils/common';
import KlarityIcon from './imgs/klarity.svg';

type AppointmentRecurringMeta = {
    recurrenceDesc?: string;
    endOccurrences?: number;
    occurrenceIndex?: number;
    occurrenceNumber?: number;
};

type AppointmentWithRecurringMeta = TAppointmentItem & AppointmentRecurringMeta;

const isPrescriptionRefillAppointment = (
    appointment?: Pick<TAppointmentItem, 'appointmentName' | 'serviceType'>,
): boolean => {
    const name = (appointment?.appointmentName || '').toLowerCase();
    const serviceType = (appointment?.serviceType || '').toLowerCase();

    // Backward/forward compatible:
    // - some API payloads use a constant key like "PRESCRIPTION_SERVICE"
    // - some payloads use human readable type like "Prescription service"
    // - some older naming mentions "prescription refill" in the title
    return (
        serviceType === 'prescription_service' ||
        serviceType === 'prescription service' ||
        serviceType === 'prescription-service' ||
        name.includes('prescription refill')
    );
};

const getRefillTexts = (params: {
    recurrenceDesc?: string;
    endOccurrences?: number;
    occurrenceIndex?: number;
    startAt?: string;
    timezone?: string;
    mode?: 'scheduled' | 'submitted';
}) => {
    const { recurrenceDesc, endOccurrences, occurrenceIndex, startAt, timezone, mode } = params;

    const calendarLeft = recurrenceDesc || 'Monthly';
    const calendarRight = endOccurrences ? `${endOccurrences} occurrences in total` : undefined;
    const refillCalendarText = calendarRight ? `${calendarLeft} | ${calendarRight}` : calendarLeft;

    const visitNo = occurrenceIndex && endOccurrences ? `Visit ${occurrenceIndex}/${endOccurrences}: ` : '';
    const dateText =
        startAt && timezone
            ? `${mode === 'submitted' ? 'Submitted on' : 'Scheduled on'} ${dayjs(startAt)
                  .tz(timezone)
                  .format('dddd, MMM D')}`
            : '';
    const refillVisitText = `${visitNo}${dateText}`.trim();

    return { refillCalendarText, refillVisitText };
};

interface AppointmentCardProps {
    item: TAppointmentItem;
    clientId?: number;
    clientName?: string;
    onViewIntakeForm?: () => void;
    onViewVisitNotes?: () => void;
    onCancelRecurring?: (item: TAppointmentItem) => void;
    listTab?: 'InProgress' | 'PastDue' | 'Upcoming' | 'Past';
    isGroupPractice?: boolean;
    client?: Prospect;
    useInModule?: boolean;
}

const AppointmentCard = (props: AppointmentCardProps) => {
    const { item, clientId, clientName, onViewIntakeForm, onViewVisitNotes, onCancelRecurring, listTab, isGroupPractice, client, useInModule } = props;
    const {
        appointmentStatus,
        appointmentName,
        duration,
        teleHealthLink,
        externalVideoLink,
        reason,
        startAt,
        endAt,
        telephone,
        contactType,
        id,
    } = item || {};
    const [expanded, setExpanded] = useState(false);
    const [isOverflow, setIsOverflow] = useState(false);
    const visitRef = useRef<HTMLDivElement>(null);
    const hiddenRef = useRef<HTMLDivElement>(null);
    const [viewAppointmentLoading, setViewAppointmentLoading] = useState(false);
    const [, myTimezone] = useTimezone();
    const [sendingIntakeReminder, setSendingIntakeReminder] = React.useState<boolean>(false);

    const showIntakeFormPending =
        item?.intakeFormStatus === EIntakeFormStatus.REQUIRED ||
        item?.intakeFormStatus === EIntakeFormStatus.IN_PROGRESS;

    const showViewIntakeForm = item?.intakeFormStatus === EIntakeFormStatus.COMPLETED;

    const isPrescriptionRefillVisit = React.useMemo(
        () => isPrescriptionRefillAppointment({ appointmentName, serviceType: item?.serviceType }),
        [appointmentName, item?.serviceType],
    );

    const { recurrenceDesc, endOccurrences, occurrenceIndex, occurrenceNumber } = (item ||
        {}) as AppointmentWithRecurringMeta;
    const refillOccurrenceIndex = occurrenceIndex ?? occurrenceNumber;

    const canSendMessage = !isGroupPractice || (isGroupPractice && client && client.boundProviders?.length === 1);

    const { refillCalendarText, refillVisitText } = React.useMemo(() => {
        return getRefillTexts({
            recurrenceDesc,
            endOccurrences,
            occurrenceIndex: refillOccurrenceIndex,
            startAt,
            timezone: myTimezone?.value as string | undefined,
            mode: listTab === 'Past' ? 'submitted' : 'scheduled',
        });
    }, [endOccurrences, recurrenceDesc, refillOccurrenceIndex, startAt, myTimezone?.value, listTab]);

    const refillStatus = React.useMemo(() => {
        if (!isPrescriptionRefillVisit) return null;
        if (useInModule) {
            const map: Record<string, { text: string; color: string; backgroundColor: string }> = {
                InProgress: { text: 'In-progress', color: '#92400E', backgroundColor: '#FEF3C7' },
                PastDue: { text: 'Past due', color: '#991B1B', backgroundColor: '#FEE2E2' },
                Upcoming: { text: 'Upcoming', color: '#1E40AF', backgroundColor: '#DBEAFE' },
                Past: { text: 'Past', color: '#065F46', backgroundColor: '#D1FAE5' },
            };
            return listTab ? map[listTab] : { text: 'In-progress', color: '#92400E', backgroundColor: '#FEF3C7' };
        }
        if (listTab === 'PastDue') return { text: 'Past due', color: '#991B1B', backgroundColor: '#FEE2E2' };
        if (listTab === 'Past') return { text: 'Completed', color: '#065F46', backgroundColor: '#D1FAE5' };
        return { text: 'In-progress', color: '#92400E', backgroundColor: '#FEF3C7' };
    }, [isPrescriptionRefillVisit, listTab, useInModule]);

    const showPaidBadge = isPrescriptionRefillVisit && listTab === 'Past';

    useEffect(() => {
        if (hiddenRef.current && visitRef.current) {
            setIsOverflow(hiddenRef.current.offsetWidth > visitRef.current.offsetWidth);
        }
    }, [reason]);

    const handleClickViewAppointment = async (appointmentId: number) => {
        if (!appointmentId) {
            return;
        }
        setViewAppointmentLoading(true);
        const res = await getCalendarAppointmentEventDetail(`${appointmentId}`);
        setViewAppointmentLoading(false);
        if (!res.error) {
            const resData: TServerCalendarAppointmentEventDetail | undefined = res?.data?.data || undefined;
            if (resData) {
                let url = `${PATH.DASHBOARD}/${PATH.CALENDAR}?aid=${resData?.id}&date=${getTimeStr(dayjs(resData?.startAt), 'MM-DD-YYYY')}`;
                if (isGroupPractice) {
                    //add provider id to url
                    url += `&providerId=${item?.providerId}`;
                }
                window.open(url, '_blank');
            }
        }
    };

    const handleSendIntakeFormReminder = async () => {
        if (!clientId) {
            message.error('Cannot find the client');
            return;
        }

        const reminderMessage = `Hi ${clientName || 'Patient'},
Just a quick reminder to complete your intake form to help us prepare for your visit. Please fill it out at your earliest convenience using the link below:
<a href="${getIntakeFormUrlByAppointmentId(id)}" target="_blank">${getIntakeFormUrlByAppointmentId(id)}</a>
If you have any questions, feel free to reach out. We're here to help!`;

        setSendingIntakeReminder(true);
        let _providerId = item?.providerId;
        let _clientId = clientId;
        if (isGroupPractice && client && client.boundProviders?.length === 1) {
            _providerId = client.boundProviders[0].providerId!;
            _clientId = client.boundProviders[0].prospectId!;
        }
        const res = await sendMessage({
            providerId: _providerId,
            clientId: _clientId,
            message: reminderMessage.replace(/<[^>]+>/g, ''),
        });
        setSendingIntakeReminder(false);

        if (!res.error) {
            message.success('Intake form reminder sent successfully!');

            //end add local message
        }
    };

    // 保险 eligibility 查不过导致的系统自动取消：状态胶囊本身和 provider 手动取消长一样，
    // 靠 tooltip 说明原因（后端 §13 明确不要从 appointmentStatus 反推）。
    // 退款那句只在后端确认退过 lead-gen fee 时说——非 lead-gen 来源的预约没有这笔钱
    const insuranceCanceled = !!item?.insuranceCheckFailedCanceled;
    const canceledTip = insuranceCanceled
        ? `This appointment was canceled because the patient’s insurance could not be verified before the visit.${
              item?.leadGenFeeRefunded ? ' Your lead-gen fee has been refunded.' : ''
          }`
        : '';
    const moduleTabLabel = React.useMemo(() => {
        const map: Record<string, { text: string; color: string; backgroundColor: string }> = {
            InProgress: { text: 'In-progress', color: '#92400E', backgroundColor: '#FEF3C7' },
            PastDue: { text: 'Past due', color: '#991B1B', backgroundColor: '#FEE2E2' },
            Upcoming: { text: 'Upcoming', color: '#1E40AF', backgroundColor: '#DBEAFE' },
            Past: { text: 'Past', color: '#065F46', backgroundColor: '#D1FAE5' },
        };
        return listTab ? map[listTab] : null;
    }, [listTab]);

    const statusPillNode = useInModule && moduleTabLabel ? (
        <span
            className={s.status}
            style={{ color: moduleTabLabel.color, backgroundColor: moduleTabLabel.backgroundColor }}
        >
            {moduleTabLabel.text}
        </span>
    ) : (
        <span
            className={s.status}
            style={
                appointmentStatus !== 'scheduled'
                    ? {
                          color: 'var(--red-800, #991b1b)',
                          backgroundColor: 'var(--red-100, #fee2e2)',
                      }
                    : {}
            }
        >
            {capitalize(appointmentStatus)}
            {insuranceCanceled && (
                <svg className={s.statusTip} viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden="true">
                    <circle cx="10" cy="10" r="8" strokeWidth="1.6" />
                    <path d="M10 6.2v4.4" strokeWidth="1.6" strokeLinecap="round" />
                    <circle cx="10" cy="13.6" r="1" fill="currentColor" stroke="none" />
                </svg>
            )}
        </span>
    );
    const statusPill = insuranceCanceled ? (
        <Tooltip title={canceledTip} overlayStyle={{ maxWidth: 320 }}>
            {statusPillNode}
        </Tooltip>
    ) : (
        statusPillNode
    );

    if (useInModule && moduleTabLabel) {
        const dateStr = startAt
            ? dayjs(startAt).tz(myTimezone?.value as string).format('dddd, MMMM D, YYYY')
            : '';
        const timeStr = startAt && endAt
            ? `${dayjs(startAt).tz(myTimezone?.value as string).format('hh:mm')} - ${dayjs(endAt).tz(myTimezone?.value as string).format('hh:mm a')} (${myTimezone?.value}) | ${duration} mins`
            : '';
        return (
            <div className={s.moduleCard}>
                <div className={s.moduleStatusPill} style={{ background: moduleTabLabel.backgroundColor, color: moduleTabLabel.color }}>
                    {moduleTabLabel.text}
                </div>
                <div className={s.moduleCardTitle}>
                    {isPrescriptionRefillVisit ? `🔁 ${appointmentName}` : appointmentName}
                </div>
                {isPrescriptionRefillVisit ? (
                    <>
                        {refillCalendarText && (
                            <div className={s.moduleInfoRow}>
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 5.25V2.25M12 5.25V2.25M5.25 8.25H12.75M3.75 15.75H14.25C15.0784 15.75 15.75 15.0784 15.75 14.25V5.25C15.75 4.42157 15.0784 3.75 14.25 3.75H3.75C2.92157 3.75 2.25 4.42157 2.25 5.25V14.25C2.25 15.0784 2.92157 15.75 3.75 15.75Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>{refillCalendarText}</span>
                            </div>
                        )}
                        {refillVisitText && (
                            <div className={s.moduleInfoRow}>
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 6V9L11.25 11.25M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>{refillVisitText}</span>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {dateStr && (
                            <div className={s.moduleInfoRow}>
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 5.25V2.25M12 5.25V2.25M5.25 8.25H12.75M3.75 15.75H14.25C15.0784 15.75 15.75 15.0784 15.75 14.25V5.25C15.75 4.42157 15.0784 3.75 14.25 3.75H3.75C2.92157 3.75 2.25 4.42157 2.25 5.25V14.25C2.25 15.0784 2.92157 15.75 3.75 15.75Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>{dateStr}</span>
                            </div>
                        )}
                        {timeStr && (
                            <div className={s.moduleInfoRow}>
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 6V9L11.25 11.25M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>{timeStr}</span>
                            </div>
                        )}
                    </>
                )}
                {showViewIntakeForm && (
                    <div className={s.vItem}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path
                                d="M6 3.75H4.5C3.67157 3.75 3 4.42157 3 5.25V14.25C3 15.0784 3.67157 15.75 4.5 15.75H12C12.8284 15.75 13.5 15.0784 13.5 14.25V13.5M6 3.75C6 4.57843 6.67157 5.25 7.5 5.25H9C9.82843 5.25 10.5 4.57843 10.5 3.75M6 3.75C6 2.92157 6.67157 2.25 7.5 2.25H9C9.82843 2.25 10.5 2.92157 10.5 3.75M10.5 3.75H12C12.8284 3.75 13.5 4.42157 13.5 5.25V7.5M15 10.5H7.5M7.5 10.5L9.75 8.25M7.5 10.5L9.75 12.75"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <div className={s.viewLink} onClick={onViewIntakeForm}>
                            View intake forms
                        </div>
                    </div>
                )}
                {item.providerTreatmentSubmitted && (
                    <div className={s.vItem}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M5.25 3H4.5C3.67157 3 3 3.67157 3 4.5V15C3 15.8284 3.67157 16.5 4.5 16.5H13.5C14.3284 16.5 15 15.8284 15 15V4.5C15 3.67157 14.3284 3 13.5 3H12.75M5.25 3C5.25 3.82843 5.92157 4.5 6.75 4.5H11.25C12.0784 4.5 12.75 3.82843 12.75 3M5.25 3C5.25 2.17157 5.92157 1.5 6.75 1.5H11.25C12.0784 1.5 12.75 2.17157 12.75 3M6 9H12M6 12H9.75" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className={s.viewLink} onClick={onViewVisitNotes}>
                            View visit notes
                        </div>
                    </div>
                )}
                <div className={s.vItem}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M9 3.75V9L11.25 11.25M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className={s.viewLink} onClick={() => handleClickViewAppointment(id)}>
                        View details
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={s.card}>
            <div className={s.cardLeft}>
                <div className={s.title}>
                    <span>{isPrescriptionRefillVisit ? `🔁 ${appointmentName}` : appointmentName}</span>
                    {isGroupPractice && item?.provider && (item.provider.firstName || item.provider.lastName) && (
                        <span className={s.fromProvider}>
                            from {`${item.provider.firstName || ''} ${item.provider.lastName || ''}`.trim()}
                        </span>
                    )}
                </div>
                <div className={s.row}>
                    <div className={s.rowItem}>
                        <span className={s.icon}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                            >
                                <path
                                    d="M6 5.25V2.25M12 5.25V2.25M5.25 8.25H12.75M3.75 15.75H14.25C15.0784 15.75 15.75 15.0784 15.75 14.25V5.25C15.75 4.42157 15.0784 3.75 14.25 3.75H3.75C2.92157 3.75 2.25 4.42157 2.25 5.25V14.25C2.25 15.0784 2.92157 15.75 3.75 15.75Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <span>
                            {isPrescriptionRefillVisit
                                ? refillCalendarText
                                : dayjs(startAt || '')
                                      .tz(myTimezone?.value as string)
                                      .format('dddd, MMMM D, YYYY')}
                        </span>
                    </div>
                    <div className={s.rowItem}>
                        <span className={s.icon}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                            >
                                <path
                                    d="M9 6V9L11.25 11.25M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <span>
                            {isPrescriptionRefillVisit
                                ? refillVisitText
                                : `${dayjs(startAt || '')
                                      .tz(myTimezone?.value as string)
                                      .format('hh:mm')} - ${dayjs(endAt || '')
                                      .tz(myTimezone?.value as string)
                                      .format('hh:mm a')} (${myTimezone?.value}) | ${duration} mins`}
                        </span>
                    </div>
                </div>
                {!isPrescriptionRefillVisit && (
                    <div className={s.row}>
                        <span className={s.icon}>
                            <img src={KlarityIcon} alt="Klarity" />
                        </span>
                        {contactType === EAppointmentContactTypeDictKey.VIDEO ? (
                            <span className={s.contactTypeWrap}>
                                Video:{' '}
                                <a
                                    className={`${s.link} ${s.linkVideo}`}
                                    href={externalVideoLink || teleHealthLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {externalVideoLink || teleHealthLink}
                                </a>{' '}
                                {(externalVideoLink || teleHealthLink) && <div className={s.linkExtraIcon} />}
                            </span>
                        ) : (
                            <span className={s.contactTypeWrap}>Tele-health: {telephone}</span>
                        )}
                    </div>
                )}

                <div className={s.row}>
                    <div className={s.icon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path
                                d="M6.75 9H11.25M6.75 12H11.25M12.75 15.75H5.25C4.42157 15.75 3.75 15.0784 3.75 14.25V3.75C3.75 2.92157 4.42157 2.25 5.25 2.25H9.43934C9.63825 2.25 9.82902 2.32902 9.96967 2.46967L14.0303 6.53033C14.171 6.67098 14.25 6.86175 14.25 7.06066V14.25C14.25 15.0784 13.5784 15.75 12.75 15.75Z"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div
                        className={expanded ? s.visitExpanded : s.visit}
                        ref={visitRef}
                        title={reason}
                        style={{
                            width: isOverflow ? '160px' : 'auto',
                        }}
                    >
                        Visit reason: {reason || '-'}
                    </div>
                    {!expanded && isOverflow && (
                        <a className={s.link} onClick={() => setExpanded(true)}>
                            {' '}
                            View all
                        </a>
                    )}
                    <div className={s.hiddenContent} ref={hiddenRef} title={reason}>
                        {reason}
                    </div>
                </div>
                {showIntakeFormPending && canSendMessage && (
                    <div className={s.vItem}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path
                                d="M6 3.75H4.5C3.67157 3.75 3 4.42157 3 5.25V14.25C3 15.0784 3.67157 15.75 4.5 15.75H12C12.8284 15.75 13.5 15.0784 13.5 14.25V13.5M6 3.75C6 4.57843 6.67157 5.25 7.5 5.25H9C9.82843 5.25 10.5 4.57843 10.5 3.75M6 3.75C6 2.92157 6.67157 2.25 7.5 2.25H9C9.82843 2.25 10.5 2.92157 10.5 3.75M10.5 3.75H12C12.8284 3.75 13.5 4.42157 13.5 5.25V7.5M15 10.5H7.5M7.5 10.5L9.75 8.25M7.5 10.5L9.75 12.75"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <div className={s.intakeFormItem}>
                            <div className={s.intakeFormItemLabel}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M8.25803 3.09956C9.02264 1.74025 10.9797 1.74025 11.7443 3.09956L17.3246 13.0201C18.0746 14.3533 17.1111 16.0006 15.5815 16.0006H4.42088C2.89123 16.0006 1.9278 14.3533 2.67773 13.0201L8.25803 3.09956ZM11.0011 13.0007C11.0011 13.553 10.5534 14.0007 10.0011 14.0007C9.44881 14.0007 9.0011 13.553 9.0011 13.0007C9.0011 12.4484 9.44881 12.0007 10.0011 12.0007C10.5534 12.0007 11.0011 12.4484 11.0011 13.0007ZM10.0011 5.00073C9.44881 5.00073 9.0011 5.44845 9.0011 6.00073V9.00073C9.0011 9.55302 9.44881 10.0007 10.0011 10.0007C10.5534 10.0007 11.0011 9.55302 11.0011 9.00073V6.00073C11.0011 5.44845 10.5534 5.00073 10.0011 5.00073Z"
                                        fill="#D97706"
                                    />
                                </svg>
                                Intake form pending
                            </div>
                            <div className={s.intakeFormItemSend}>
                                <Button
                                    className={s.intakeFormReminderBtn}
                                    loading={sendingIntakeReminder}
                                    onClick={handleSendIntakeFormReminder}
                                    type="text"
                                >
                                    <span>Send reminder</span>
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        overlayInnerStyle={{
                                            width: '330px',
                                            padding: '16px',
                                            borderRadius: '24px',
                                        }}
                                        color="#ffffff"
                                        title={
                                            <div className={s.tooltipContent} style={{ color: '#333' }}>
                                                <div className={s.tooltipTitle}>
                                                    📩 Intake form reminder message preview
                                                </div>
                                                <div className={s.tooltipMessage}>
                                                    <p>Hi [Patient&apos;s Name],</p>
                                                    <p>
                                                        Just a quick reminder to complete your intake form to help us
                                                        prepare for your visit. Please fill it out at your earliest
                                                        convenience using the link below:
                                                    </p>
                                                    <p className={s.tooltipLink}>[Intake Form URL]</p>
                                                    <p>
                                                        If you have any questions, feel free to reach out. We&apos;re
                                                        here to help!
                                                    </p>
                                                </div>
                                            </div>
                                        }
                                        placement="top"
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                            className={s.questionIcon}
                                        >
                                            <path
                                                d="M4.48511 5C4.85118 4.22321 5.83895 3.66667 7.00004 3.66667C8.4728 3.66667 9.6667 4.5621 9.6667 5.66667C9.6667 6.59963 8.81496 7.38338 7.66285 7.6044C7.30125 7.67377 7.00004 7.96514 7.00004 8.33333M7 10.3333H7.00667M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
                                                stroke="#6B7280"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </Tooltip>
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                {showViewIntakeForm && (
                    <div className={s.vItem}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.25803 3.09956C9.02264 1.74025 10.9797 1.74025 11.7443 3.09956L17.3246 13.0201C18.0746 14.3533 17.1111 16.0006 15.5815 16.0006H4.42088C2.89123 16.0006 1.9278 14.3533 2.67773 13.0201L8.25803 3.09956ZM11.0011 13.0007C11.0011 13.553 10.5534 14.0007 10.0011 14.0007C9.44881 14.0007 9.0011 13.553 9.0011 13.0007C9.0011 12.4484 9.44881 12.0007 10.0011 12.0007C10.5534 12.0007 11.0011 12.4484 11.0011 13.0007ZM10.0011 5.00073C9.44881 5.00073 9.0011 5.44845 9.0011 6.00073V9.00073C9.0011 9.55302 9.44881 10.0007 10.0011 10.0007C10.5534 10.0007 11.0011 9.55302 11.0011 9.00073V6.00073C11.0011 5.44845 10.5534 5.00073 10.0011 5.00073Z"
                                fill="#D97706"
                            />
                        </svg>
                        <div className={s.viewLink} onClick={onViewIntakeForm}>
                            View intake forms
                        </div>
                    </div>
                )}
            </div>
            <div className={s.cardRight}>
                {isPrescriptionRefillVisit ? (
                    <>
                        {showPaidBadge ? (
                            <div className={s.badgeRow}>
                                <span className={s.status} style={{ color: '#065F46', backgroundColor: '#D1FAE5' }}>
                                    Paid
                                </span>
                                <span
                                    className={s.status}
                                    style={
                                        refillStatus
                                            ? {
                                                  color: refillStatus.color,
                                                  backgroundColor: refillStatus.backgroundColor,
                                              }
                                            : {}
                                    }
                                >
                                    {refillStatus?.text || 'Completed'}
                                </span>
                            </div>
                        ) : (
                            <span
                                className={s.status}
                                style={
                                    refillStatus
                                        ? {
                                              color: refillStatus.color,
                                              backgroundColor: refillStatus.backgroundColor,
                                          }
                                        : {}
                                }
                            >
                                {refillStatus?.text || 'In-progress'}
                            </span>
                        )}
                    </>
                ) : (
                    statusPill
                )}
                <div className={s.header}>
                    {isPrescriptionRefillVisit && listTab === 'PastDue' && onCancelRecurring && (
                        <Button className={s.cancelBtn} onClick={() => onCancelRecurring(item)}>
                            Cancel
                        </Button>
                    )}
                    <Button
                        className={s.viewBtn}
                        loading={viewAppointmentLoading}
                        onClick={() => handleClickViewAppointment(id)}
                    >
                        {isPrescriptionRefillVisit && listTab === 'InProgress' ? 'Resume' : 'View'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AppointmentCard;
